import axios from 'axios';
import { installTracer } from '../devtools/tracer';

const API_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:8000/api'
  : '/api';

const client = axios.create({
  baseURL: API_BASE,
});

// Install API tracer (dev only — no-op in production)
installTracer(client);

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Token ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const auth = {
  register: (data) => client.post('/auth/register/', data),
  login: (data) => client.post('/auth/login/', data),
  logout: () => client.post('/auth/logout/'),
  me: () => client.get('/auth/me/'),
};

export const datasets = {
  list: () => client.get('/datasets/'),
  get: (id) => client.get(`/datasets/${id}/`),
  delete: (id) => client.delete(`/datasets/${id}/`),
  upload: (formData) =>
    client.post('/datasets/upload/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  candles: (datasetId, offset = 0, limit = 5000) =>
    client.get(`/datasets/${datasetId}/candles/`, { params: { offset, limit } }),
};

export const sessions = {
  list: (params) => client.get('/sessions/', { params }),
  create: (data) => client.post('/sessions/create/', data),
  get: (id) => client.get(`/sessions/${id}/`),
  updateProgress: (id, currentBarIndex) =>
    client.patch(`/sessions/${id}/progress/`, { current_bar_index: currentBarIndex }),
  complete: (id, data) => client.post(`/sessions/${id}/complete/`, data || {}),
  abandon: (id) => client.post(`/sessions/${id}/abandon/`),
  delete: (id) => client.delete(`/sessions/${id}/delete/`),
  saveIndicatorConfig: (id, config) =>
    client.put(`/sessions/${id}/indicators/`, { indicator_config: config }),
  saveDrawingConfig: (id, config) =>
    client.put(`/sessions/${id}/drawings/`, { drawing_config: config }),
};

export const trades = {
  list: (sessionId) => client.get(`/sessions/${sessionId}/trades/`),
  create: (sessionId, data) =>
    client.post(`/sessions/${sessionId}/trades/create/`, data),
  close: (sessionId, tradeId, data) =>
    client.patch(`/sessions/${sessionId}/trades/${tradeId}/close/`, data),
  modify: (sessionId, tradeId, data) =>
    client.patch(`/sessions/${sessionId}/trades/${tradeId}/modify/`, data),
  detail: (sessionId, tradeId) =>
    client.get(`/sessions/${sessionId}/trades/${tradeId}/detail/`),
  update: (sessionId, tradeId, data) =>
    client.patch(`/sessions/${sessionId}/trades/${tradeId}/update/`, data),
};

export const tags = {
  list: () => client.get('/tags/'),
  create: (data) => client.post('/tags/', data),
  update: (id, data) => client.patch(`/tags/${id}/`, data),
  delete: (id) => client.delete(`/tags/${id}/`),
};

export const tradeNotes = {
  create: (sessionId, tradeId, formData) =>
    client.post(`/sessions/${sessionId}/trades/${tradeId}/notes/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: (sessionId, tradeId, noteId) =>
    client.delete(`/sessions/${sessionId}/trades/${tradeId}/notes/${noteId}/`),
};

export const journal = {
  list: (params) => client.get('/journal/', { params }),
};

export const analytics = {
  summary: (params) => client.get('/analytics/summary/', { params }),
};

export const economicCalendar = {
  get: (from, to, currencies, impact) => client.get('/economic-calendar/', {
    params: { from, to, currencies: currencies || undefined, impact: impact || undefined },
  }),
};

export const notebooks = {
  list: (params) => client.get('/notebooks/', { params }),
  create: (data) => client.post('/notebooks/', data),
  get: (id) => client.get(`/notebooks/${id}/`),
  update: (id, data) => client.patch(`/notebooks/${id}/`, data),
  delete: (id) => client.delete(`/notebooks/${id}/`),
  uploadImage: (id, formData) => client.post(`/notebooks/${id}/upload/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  categories: () => client.get('/notebooks/categories/'),
};

export const playbooks = {
  list: () => client.get('/playbooks/'),
  create: (data) => client.post('/playbooks/', data),
  get: (id) => client.get(`/playbooks/${id}/`),
  update: (id, data) => client.patch(`/playbooks/${id}/`, data),
  delete: (id) => client.delete(`/playbooks/${id}/`),
};

export const challenges = {
  list: (params) => client.get('/challenges/', { params }),
};

export const admin = {
  dashboard: () => client.get('/auth/admin/dashboard/'),
};

export const marketData = {
  popular: () => client.get('/market-data/popular/'),
  sources: () => client.get('/market-data/sources/'),
  searchInstruments: (source, query) =>
    client.get('/market-data/instruments/', { params: { source, q: query } }),
  startDownload: (data) => client.post('/market-data/download/', data),
  getTask: (id) => client.get(`/market-data/tasks/${id}/`),
  listTasks: () => client.get('/market-data/tasks/'),
};

export default client;
